import type { SignalPriority } from '@parcel/types';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  cn,
} from '@parcel/ui';
import { ChevronDown } from 'lucide-react';
import { PRIORITY_DOT_CLASS, priorityLabel } from '../../lib/signal-labels.js';

const PRIORITIES: SignalPriority[] = ['high', 'medium', 'low'];

export function PriorityDot({ priority }: { priority: SignalPriority }) {
  return (
    <span
      className={cn('h-2 w-2 shrink-0 rounded-full', PRIORITY_DOT_CLASS[priority])}
      aria-hidden
    />
  );
}

/** Priority picker shared by onboarding, the settings table rows, and the new-signal dialog. */
export function PriorityDropdown({
  value,
  onChange,
  variant = 'default',
}: {
  value: SignalPriority;
  onChange: (next: SignalPriority) => void;
  /** `cell` is the in-row select used by the seat tier and ICP tier columns. */
  variant?: 'default' | 'cell';
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'justify-between gap-2',
            variant === 'cell' ? 'h-8 w-full px-2 font-normal' : 'w-28',
          )}
        >
          <span className="flex items-center gap-2">
            <PriorityDot priority={value} />
            {priorityLabel(value)}
          </span>
          <ChevronDown
            className={cn('h-3.5 w-3.5 shrink-0', variant === 'cell' && 'opacity-60')}
            aria-hidden
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-36">
        <DropdownMenuRadioGroup
          value={value}
          onValueChange={(next) => onChange(next as SignalPriority)}
        >
          {PRIORITIES.map((priority) => (
            <DropdownMenuRadioItem key={priority} value={priority} className="gap-2">
              <PriorityDot priority={priority} />
              {priorityLabel(priority)}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
